import React, { useState, useEffect } from 'react';

// Configurazione di default
const DEFAULT_CONFIG = {
  allowedDomains: ['https://bem.betacomservices.com'],
  allowedPaths: ['/'], // Percorsi specifici autorizzati (opzionale)
  redirectUrl: 'https://bem.betacomservices.com/',
  checkInterval: 10000, // Controllo ogni 10 secondi
  allowDirectAccess: false, // Per sviluppo locale
  debugMode: false,
  showLoadingScreen: true,
  autoRedirect: false, // Disabilita redirect automatico
  autoRedirectDelay: 5000 // Delay prima del redirect automatico (se abilitato)
};

// Hook personalizzato per il controllo accesso
export const useAccessControl = (userConfig = {}) => {
  const config = { ...DEFAULT_CONFIG, ...userConfig };
  const [accessStatus, setAccessStatus] = useState('checking'); // 'checking', 'allowed', 'denied'
  const [denialReason, setDenialReason] = useState('');

  const log = (message, type = 'info') => {
    if (config.debugMode) {
      console[type]('[ACCESS CONTROL]', message);
    }
  };

  const checkReferrer = () => {
    const referrer = document.referrer;
    log(`Referrer: ${referrer}`);

    if (!referrer) {
      log('Nessun referrer trovato', 'warn');
      return { allowed: false, reason: 'Accesso diretto non consentito' };
    }

    if (!config.allowedDomains.some(domain => referrer.startsWith(domain))) {
      log(`Referrer non autorizzato: ${referrer}`, 'warn');
      return { allowed: false, reason: `Referrer non autorizzato: ${referrer}` };
    }

    return { allowed: true, reason: 'Referrer valido' };
  };

  const checkWindowParent = () => {
    try {
      if (window.top !== window.self) {
        log('Pagina caricata in iframe');
        
        try {
          const parentOrigin = window.parent.location.origin;
          const isAllowed = config.allowedDomains.some(domain => 
            domain.replace(/\/$/, '') === parentOrigin
          );
          
          if (!isAllowed) {
            return { allowed: false, reason: 'Iframe da dominio non autorizzato' };
          }
        } catch (e) {
          log('Cross-origin iframe detected');
          return { allowed: false, reason: 'Cross-origin iframe non autorizzato' };
        }
      }
      return { allowed: true, reason: 'Parent window autorizzato' };
    } catch (e) {
      log(`Errore controllo parent: ${e.message}`, 'error');
      return { allowed: false, reason: 'Errore verifica parent window' };
    }
  };

  const performAccessCheck = () => {
    log('Inizio controllo accesso');

    // BYPASS COMPLETO per sviluppo
    if (config.allowDirectAccess) {
      log('allowDirectAccess = true - BYPASS di tutti i controlli');
      return { allowed: true, reason: 'Modalità sviluppo - accesso sempre consentito' };
    }

    const referrerCheck = checkReferrer();
    if (!referrerCheck.allowed) {
      return referrerCheck;
    }

    const parentCheck = checkWindowParent();
    if (!parentCheck.allowed) {
      return parentCheck;
    }

    return { allowed: true, reason: 'Tutti i controlli superati' };
  };

  useEffect(() => {
    const checkAccess = () => {
      const result = performAccessCheck();
      
      if (result.allowed) {
        log(`Accesso consentito: ${result.reason}`);
        setAccessStatus('allowed');
      } else {
        log(`Accesso negato: ${result.reason}`, 'warn');
        setAccessStatus('denied');
        setDenialReason(result.reason);
      }
    };

    // Controllo iniziale
    checkAccess();

    // Controllo continuo
    const interval = setInterval(checkAccess, config.checkInterval);

    return () => clearInterval(interval);
  }, [config]);

  return {
    accessStatus,
    denialReason,
    isAllowed: accessStatus === 'allowed',
    isChecking: accessStatus === 'checking',
    isDenied: accessStatus === 'denied'
  };
};

// Componente di blocco accesso
const AccessDeniedScreen = ({ reason, redirectUrl, autoRedirect, autoRedirectDelay }) => {
  const [countdown, setCountdown] = useState(Math.ceil(autoRedirectDelay / 1000));

  useEffect(() => {
    if (!autoRedirect) return; // Non fare nulla se autoRedirect è disabilitato

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.replace(redirectUrl);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [redirectUrl, autoRedirect, autoRedirectDelay]);

  const handleRedirect = () => {
    window.location.replace(redirectUrl);
  };

  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      textAlign: 'center',
      padding: '50px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      minHeight: '100vh',
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <h1 style={{
        fontSize: '2.5em',
        marginBottom: '20px',
        textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
      }}>
        🚫 Accesso Negato
      </h1>
      <p style={{ fontSize: '1.2em', maxWidth: '600px', lineHeight: '1.6' }}>
        <strong>Motivo:</strong> {reason}
      </p>
      <p style={{ fontSize: '1.2em', maxWidth: '600px', lineHeight: '1.6' }}>
        Questa pagina può essere accessibile solo da domini autorizzati
      </p>
      
      {autoRedirect ? (
        <p style={{ fontSize: '1.1em', marginTop: '20px' }}>
          Verrai reindirizzato automaticamente tra <strong>{countdown}</strong> secondi...
        </p>
      ) : (
        <p style={{ fontSize: '1.1em', marginTop: '20px', opacity: 0.8 }}>
          Contatta l'amministratore per assistenza
        </p>
      )}
      
      {/* <button 
        onClick={handleRedirect}
        style={{
          background: '#4CAF50',
          color: 'white',
          padding: '15px 30px',
          border: 'none',
          borderRadius: '5px',
          fontSize: '1.1em',
          cursor: 'pointer',
          marginTop: '20px',
          transition: 'background 0.3s'
        }}
        onMouseOver={(e) => e.target.style.background = '#45a049'}
        onMouseOut={(e) => e.target.style.background = '#4CAF50'}
      >
        Vai al sito autorizzato
      </button> */}
    </div>
  );
};

// Componente di loading
const LoadingScreen = () => {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #3498db',
        borderRadius: '50%',
        width: '50px',
        height: '50px',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
      }} />
      <p style={{ fontSize: '1.2em', color: '#666' }}>
        Verifica accesso in corso...
      </p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// HOC (Higher Order Component) per proteggere componenti
export const withAccessControl = (WrappedComponent, userConfig = {}) => {
  return function ProtectedComponent(props) {
    const config = { ...DEFAULT_CONFIG, ...userConfig };
    const { accessStatus, denialReason, isAllowed, isChecking } = useAccessControl(config);

    if (isChecking && config.showLoadingScreen) {
      return <LoadingScreen />;
    }

    if (!isAllowed) {
      return (
        <AccessDeniedScreen 
          reason={denialReason}
          redirectUrl={config.redirectUrl}
          autoRedirect={config.autoRedirect}
          autoRedirectDelay={config.autoRedirectDelay}
        />
      );
    }

    return <WrappedComponent {...props} />;
  };
};

// Componente wrapper principale
export const AccessControlProvider = ({ children, config = {} }) => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const { accessStatus, denialReason, isAllowed, isChecking } = useAccessControl(finalConfig);

  if (isChecking && finalConfig.showLoadingScreen) {
    return <LoadingScreen />;
  }

  if (!isAllowed) {
    return (
      <AccessDeniedScreen 
        reason={denialReason}
        redirectUrl={finalConfig.redirectUrl}
        autoRedirect={finalConfig.autoRedirect}
        autoRedirectDelay={finalConfig.autoRedirectDelay}
      />
    );
  }

  return <>{children}</>;
};

// Export di default
const AccessControl = {
  useAccessControl,
  withAccessControl,
  AccessControlProvider,
  DEFAULT_CONFIG
};

export default AccessControl;